/**
 * «Duplicar» a goods DeCA — #178.
 *
 * A sibling of `DecaEditScreen`, on the same **write** surface, and like it only a loader: it reads
 * the source and hands it to `GoodsCreateForm` as `source`, which fills the form with a copy and
 * submits it as an ordinary create, so the copy gets its own number, QR and CSV and the source is
 * never written. Unlike the editor it takes a document in any status, cancelled and replaced
 * included, as agreed with the Client: nothing is done to the source, so its status does not
 * matter. For the same reason its `ETag` is not passed on.
 */

import { useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { useAuth } from '../app/auth.tsx'
import { useDeca } from '../app/queries.ts'
import { abilitiesFor } from '../deca/roles.ts'
import { offeredFamilies } from '../deca/transportType.ts'
import { AppShell } from '../shell/AppShell.tsx'
import { Card, ErrorState, LoadingRows, NoAccessState } from '../ui/primitives.tsx'
import { GoodsCreateForm } from './GoodsCreateForm.tsx'

export function DecaDuplicateScreen() {
  const { t } = useTranslation()
  const { decaId } = useParams({ strict: false }) as { decaId: string }
  const { principal } = useAuth()
  const abilities = abilitiesFor(principal)

  const query = useDeca(decaId)
  const source = query.data?.data

  // #220: a copy of a type the company is not offered is not offered by its address either.
  if (!abilities.writeDecas || !offeredFamilies(principal).goods) {
    return (
      <AppShell title={t('duplicate.decaTitle')}>
        <Card>
          <NoAccessState />
        </Card>
      </AppShell>
    )
  }

  if (query.isLoading) {
    return (
      <AppShell title={t('duplicate.decaTitle')}>
        <Card>
          <LoadingRows rows={6} columns={3} />
        </Card>
      </AppShell>
    )
  }

  // Once the source is in, the form stays: a failed background refetch must not replace a copy the
  // user is filling in. The error state is for when there is nothing to copy.
  if (!source) {
    return (
      <AppShell title={t('duplicate.decaTitle')}>
        <Card>
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </Card>
      </AppShell>
    )
  }

  return (
    <AppShell title={t('duplicate.decaTitle')}>
      <GoodsCreateForm source={source} />
    </AppShell>
  )
}
